import React, { useEffect, useState } from "react";
import ModulePage from "@/components/ModulePage";
import ImageThumb from "@/components/ImageThumb";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { AlertTriangle, Layers } from "lucide-react";
import { usePerms } from "@/lib/auth";

const columns = [
  {
    key: "image_url",
    label: "Image",
    formatter: (r) =>
      r.image_url ? (
        <ImageThumb src={r.image_url} size={64} testId={`mapping-thumb-${r.id}`} />
      ) : (
        <span className="text-slate-400">—</span>
      ),
  },
  { key: "system_sku", label: "System SKU", mono: true },
  { key: "marketplace_identifier", label: "Marketplace Identifier", mono: true },
  { key: "warehouse_name", label: "Warehouse" },
  { key: "account_id", label: "Account ID", mono: true },
  {
    key: "product_url",
    label: "Product URL",
    formatter: (r) =>
      r.product_url ? (
        <a
          href={r.product_url}
          target="_blank"
          rel="noreferrer"
          onClick={(e) => e.stopPropagation()}
          className="text-blue-600 hover:underline font-mono text-[11px]"
        >
          {r.product_url.length > 32 ? r.product_url.slice(0, 32) + "…" : r.product_url}
        </a>
      ) : (
        "—"
      ),
  },
];

const fields = [
  { key: "system_sku", label: "System SKU" },
  { key: "marketplace_identifier", label: "Marketplace Identifier" },
  { key: "warehouse_name", label: "Warehouse Name" },
  { key: "account_id", label: "Account ID" },
  { key: "image_url", label: "Image URL", type: "text" },
  { key: "product_url", label: "Product URL", type: "text" },
];

function MappingAdminBar() {
  const { isAdmin } = usePerms();
  const [busy, setBusy] = useState(false);
  if (!isAdmin) return null;

  const wipeAll = async () => {
    const phrase = "WIPE ALL MAPPINGS";
    const entry = window.prompt(
      `This will permanently delete EVERY mapping row.\nType "${phrase}" to confirm.`,
    );
    if (entry !== phrase) {
      if (entry !== null) toast.error("Confirmation phrase did not match — wipe cancelled.");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/mapping/admin/wipe", { confirm: phrase });
      toast.success(`Wiped ${data.deleted} mapping rows.`);
      window.location.reload();
    } catch (e) {
      toast.error(formatApiError(e));
    }
    setBusy(false);
  };

  const dedup = async () => {
    if (!window.confirm(
      "Deduplicate mappings?\nAny duplicate rows for the same (System SKU, Marketplace Identifier) pair will be collapsed to a single row (newest kept).",
    )) return;
    setBusy(true);
    try {
      const { data } = await api.post("/mapping/admin/dedup", {});
      toast.success(
        `Deduped: found ${data.duplicate_pairs_found} pair(s), removed ${data.deleted} row(s). ${data.remaining_rows} rows remain.`,
      );
      window.location.reload();
    } catch (e) {
      toast.error(formatApiError(e));
    }
    setBusy(false);
  };

  return (
    <>
      <button
        type="button"
        onClick={dedup}
        disabled={busy}
        data-testid="mapping-dedup-btn"
        className="h-8 px-3 text-[12px] font-medium rounded-sm inline-flex items-center gap-1.5 border border-amber-300 bg-amber-50 text-amber-800 hover:bg-amber-100 disabled:opacity-50"
        title="Admin only — collapse duplicate (SKU, Marketplace ID) rows"
      >
        <Layers size={13} /> Dedup
      </button>
      <button
        type="button"
        onClick={wipeAll}
        disabled={busy}
        data-testid="mapping-wipe-all-btn"
        className="h-8 px-3 text-[12px] font-medium rounded-sm inline-flex items-center gap-1.5 border border-red-300 bg-red-50 text-red-700 hover:bg-red-100 disabled:opacity-50"
        title="Admin only — permanently delete every mapping row"
      >
        <AlertTriangle size={13} /> Wipe All
      </button>
    </>
  );
}

export default function Mapping() {
  const { allowedWarehouses } = usePerms();
  // Mar 1 2026 — fetch every warehouse defined in the system so the top-bar
  // Warehouse dropdown shows every option (including warehouses that have
  // zero mappings yet). Sourced from the canonical Warehouse table, not from
  // mapping rows, so ops sees a stable list. Narrowed to the user's
  // ``warehouse_names`` allowlist so a limited-scope user can't sidestep it.
  const [warehouses, setWarehouses] = useState([]);
  useEffect(() => {
    api.get("/warehouse")
      .then((r) => {
        const items = Array.isArray(r.data) ? r.data : (r.data?.items || []);
        setWarehouses(items);
      })
      .catch(() => {});
  }, []);
  const whOptions = (() => {
    const all = warehouses.map((w) => w.name).filter(Boolean);
    const allow = allowedWarehouses();
    return allow ? all.filter((n) => allow.includes(n)) : all;
  })();
  return (
    <ModulePage
      moduleKey="mapping"
      title="Mapping"
      subtitle="SKU ↔ Marketplace identifier"
      columns={columns}
      fields={fields}
      filterFields={["system_sku", "marketplace_identifier", "warehouse_name", "account_id", "image_url", "product_url"]}
      topBarFilters={[
        {
          key: "warehouse_name",
          label: "Warehouse",
          options: whOptions,
        },
      ]}
      extraToolbar={<MappingAdminBar />}
      rowActions
    />
  );
}
